import React, { useMemo, useRef, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import { Center, Html, useGLTF, useScroll } from '@react-three/drei';
import * as THREE from 'three';
import { cutIndexForPage, PRIME_CUTS, SCROLL_PAGES } from './glbCatalog.js';
import { useGlbStatus } from './glbStatus.jsx';

function prepareScene(scene) {
  const g = scene.clone(true);
  const box = new THREE.Box3().setFromObject(g);
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z, 0.001);
  const uniform = 2.25 / maxDim;

  g.traverse((child) => {
    if (!child.isMesh) return;
    child.castShadow = true;
    child.receiveShadow = true;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach((m) => {
      if (!m) return;
      if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
      if (m.map) m.map.anisotropy = 8;
      if (m.isMeshStandardMaterial || m.isMeshPhysicalMaterial) {
        m.envMapIntensity = 1.35;
        if (m.roughness !== undefined) m.roughness = Math.min(1, m.roughness * 0.88 + 0.06);
        if (m.metalness !== undefined) m.metalness = Math.min(0.35, m.metalness ?? 0);
      }
    });
  });

  g.scale.setScalar(uniform);
  return g;
}

function CutLayer({ path }) {
  const { scene } = useGLTF(path);
  const root = useMemo(() => prepareScene(scene), [scene]);
  return (
    <Center>
      <primitive object={root} />
    </Center>
  );
}

function CutsRig({ cuts }) {
  const parentRef = useRef(null);
  const cutGroupRefs = useRef([]);
  const scroll = useScroll();
  const n = cuts.length;

  const cyclicDistance = (from, to, count) => {
    if (count <= 1) return 0;
    let d = from - to;
    const half = count / 2;
    if (d > half) d -= count;
    if (d < -half) d += count;
    return d;
  };

  useFrame((state) => {
    if (!parentRef.current) return;
    const t = scroll.offset;
    const breathe = Math.sin(state.clock.elapsedTime * 0.35) * 0.02;
    parentRef.current.rotation.y = t * Math.PI * 2.35 + breathe;
    // Keep full model visible, but avoid exposing the underside.
    const heroPitch = Math.sin(t * Math.PI * 2) * 0.16 + 0.2;
    parentRef.current.rotation.x = THREE.MathUtils.clamp(heroPitch, 0.04, 0.38);
    parentRef.current.rotation.z = Math.sin(t * Math.PI * 1.5) * 0.14;
    parentRef.current.position.y = Math.sin(t * Math.PI * 2) * 0.12;

    const pageFloat = scroll.offset * (SCROLL_PAGES - 1);
    const page = Math.min(SCROLL_PAGES - 1, Math.max(0, Math.round(pageFloat)));
    const active = cutIndexForPage(page, n);

    cutGroupRefs.current.forEach((g, i) => {
      if (!g) return;
      const offset = cyclicDistance(i, active, n);
      const targetX = offset * 2.9;
      const targetZ = Math.abs(offset) * -1.1;
      const targetScale = i === active ? 1 : 0.76;
      const targetY = Math.abs(offset) * -0.05;

      g.position.x = THREE.MathUtils.lerp(g.position.x, targetX, 0.08);
      g.position.y = THREE.MathUtils.lerp(g.position.y, targetY, 0.08);
      g.position.z = THREE.MathUtils.lerp(g.position.z, targetZ, 0.08);
      g.scale.setScalar(THREE.MathUtils.lerp(g.scale.x || 1, targetScale, 0.11));
      g.rotation.y += 0.004 + Math.abs(offset) * 0.0015;
    });
  });

  return (
    <group ref={parentRef}>
      {cuts.map((cut, i) => (
        <group
          key={cut.path}
          ref={(el) => (cutGroupRefs.current[i] = el)}
          position={[i * 2.9, 0, -Math.abs(i) * 1.1]}
        >
          <Suspense fallback={null}>
            <CutLayer path={cut.path} />
          </Suspense>
        </group>
      ))}
    </group>
  );
}

function useScrollMotion(groupRef) {
  const scroll = useScroll();
  useFrame((state) => {
    if (!groupRef.current) return;
    const t = scroll.offset;
    const breathe = Math.sin(state.clock.elapsedTime * 0.35) * 0.02;
    groupRef.current.rotation.y = t * Math.PI * 2.35 + breathe;
    const heroPitch = Math.sin(t * Math.PI * 2) * 0.16 + 0.2;
    groupRef.current.rotation.x = THREE.MathUtils.clamp(heroPitch, 0.04, 0.38);
    groupRef.current.rotation.z = Math.sin(t * Math.PI * 1.5) * 0.14;
    groupRef.current.position.y = Math.sin(t * Math.PI * 2) * 0.12;
  });
}

function LoadingRing() {
  const group = useRef(null);
  useScrollMotion(group);
  return (
    <group ref={group}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.85, 0.03, 12, 48]} />
        <meshBasicMaterial color="#dc2626" opacity={0.45} transparent />
      </mesh>
      <Html center style={{ pointerEvents: 'none' }}>
        <p className="text-sm font-medium text-gray-600" style={{ fontFamily: 'system-ui, sans-serif' }}>
          Loading 3D models…
        </p>
      </Html>
    </group>
  );
}

function MissingGlbHint({ availability = [] }) {
  const group = useRef(null);
  useScrollMotion(group);
  return (
    <group ref={group}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.15, 0.018, 12, 64]} />
        <meshBasicMaterial color="#dc2626" opacity={0.22} transparent />
      </mesh>
      <Html center transform distanceFactor={9} style={{ pointerEvents: 'auto' }}>
        <div
          className="w-[min(22rem,calc(100vw-2rem))] rounded-lg border border-red-200 bg-white px-5 py-4 text-center shadow-lg"
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
        >
          <p className="text-sm font-semibold text-red-700">3D cuts not found</p>
          <p className="mt-2 text-xs leading-relaxed text-gray-600">
            Add these files under <code className="rounded bg-red-50 px-1 text-red-900">landingpage/public/</code> then refresh:
          </p>
          <ul className="mt-3 space-y-1.5 text-left text-[11px] text-gray-700">
            {PRIME_CUTS.map((c, i) => (
              <li key={c.path} className="break-all font-mono">
                {c.path.replace(/^\//, '')}
                {availability[i] === false && (
                  <span className="ml-2 font-sans text-red-600">— missing</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Html>
    </group>
  );
}

function PartialNotice({ availability }) {
  const missing = PRIME_CUTS.filter((_, i) => !availability[i]).map((c) => c.name);
  if (missing.length === 0) return null;
  return (
    <Html position={[0, 1.85, 0]} center transform distanceFactor={12} style={{ pointerEvents: 'none' }}>
      <div
        className="max-w-xs rounded-lg border border-red-200 bg-white/95 px-3 py-2 text-center text-[11px] text-red-800 shadow-md"
        style={{ fontFamily: 'system-ui, sans-serif' }}
      >
        Some cuts are still missing: {missing.join(', ')}.
      </div>
    </Html>
  );
}

class GltfErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <MissingGlbHint availability={this.props.availability} />;
    }
    return this.props.children;
  }
}

export function MeatModel() {
  const { phase, visibleCuts, availability } = useGlbStatus();

  if (phase === 'missing' || visibleCuts.length === 0) {
    return <MissingGlbHint availability={availability} />;
  }

  return (
    <group>
      {phase === 'partial' && <PartialNotice availability={availability} />}
      <GltfErrorBoundary availability={availability}>
        <Suspense fallback={<LoadingRing />}>
          <CutsRig cuts={visibleCuts} />
        </Suspense>
      </GltfErrorBoundary>
    </group>
  );
}
